import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {animated, useTransition} from 'react-spring';
import {useTranslation} from 'react-i18next';
import BaseTask from './base_task/BaseTask';
import EmployeeSurvey from './employee_survey/EmployeeSurvey';
import ConfirmationClaimPopup from '../../ConfirmationClaimPopup';
import {useAppSelector} from '../../../../store/hooks';
import {currentUserIsAssignee, shouldShowClaimButton} from '../../helpers';
import {
  calculateAlertRange
} from '../../../common/time_progress_bar/helpers/calculateAlertRange';
import {AlertSettingsProps} from '../../../settings/alert_alarm/types';
import {TaskCode, ZoomGridOptions} from '../../../../constants/tasks';
import {
  CurrentTask as CurrentTaskType,
  ProcessTask,
  SuccessClaimCallback,
  TaskToClaim
} from '../../types';
import {ProcessCode} from '../../../processes/types';
import {formatAnswers} from './common/helpers/formatAnswers';
import {completeTask} from '../../../../api/tasks';
import {useSnackbarHook} from '../../../../hooks/useSnackbar';

type CurrentTaskProps = {
  task: CurrentTaskType,
  successClaimCallback?: SuccessClaimCallback,
  taskAlertData: Nullable<AlertSettingsProps>,
  addCompletedTaskToArray?: (task) => void,
  notRunningStatus: boolean,
  runningTasks?: ProcessTask[],
  zoomToElement?: (id: string, scale: number) => void,
  noAnimation?: boolean
}

const CurrentTask: FC<CurrentTaskProps> = ({
  task, successClaimCallback, zoomToElement,
  taskAlertData, notRunningStatus, noAnimation,
  addCompletedTaskToArray, runningTasks
}) => {
  const {t} = useTranslation('tasks');
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const {
    groups: userGroups = [],
    id: userId = null
  } = useAppSelector((state) => state.user);

  const [taskToClaim, setTaskToClaim] = useState<Nullable<TaskToClaim>>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    claimed, groups, assignee, id: taskId,
    status, targetTime, type, formFields,
    process
  } = task;
  const isRunning = status?.code === ProcessCode.RUNNING;

  const alertRange = useMemo(() => {
    if (!taskAlertData) return 0;

    const {relative, relativeRange, absoluteRange} = taskAlertData;
    return calculateAlertRange(
        relative,
        relativeRange,
        absoluteRange,
        targetTime
    );
  }, [JSON.stringify(taskAlertData)]);

  const hasClaimButton = useMemo(() => shouldShowClaimButton({
    taskGroups: groups, userGroups, userId, taskAssignee: assignee, isRunning
  }), [groups, userGroups, userId, assignee]);

  const isCurrentUserAssignee = currentUserIsAssignee(assignee, userId);

  const handleOpenClaimPopup = useCallback(() => {
    if (taskId) {
      setTaskToClaim({
        id: Number(taskId),
        processId: process?.id,
        isClaimed: claimed
      });
    }
  }, [claimed, taskId, process?.id]);

  const handleCloseClaimPopup = useCallback(() => {
    setTaskToClaim(null);
  }, []);

  const submitTask = useCallback(async (values) => {
    setIsSubmitting(true);
    const answers = formatAnswers(values, formFields);

    try {
      await completeTask(taskId, answers);
      addCompletedTaskToArray && addCompletedTaskToArray(task);
      addSuccessToast(t('completeTaskToastMessage'));
    } catch (e) {
      addErrorToast(e.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [taskId, task]);


  const taskProps = {
    task,
    hasClaimButton,
    isCurrentUserAssignee,
    isRunning,
    handleOpenClaimPopup,
    alertRange,
    submitTask,
    isSubmitting,
    notRunningStatus
  };

  const currentTaskTransition = useTransition(task, null, {
    from: {opacity: 0},
    enter: {opacity: 1},
    leave: {opacity: 0},
    config: {tension: 500, friction: 40},
    immediate: noAnimation || false
  });

  useEffect(() => {
    let timeout;

    if (zoomToElement && runningTasks?.length) {
      const elementToZoom = runningTasks[0];
      const zoomScale = runningTasks.length &&
        runningTasks.length > 1 ?
          ZoomGridOptions.MIN_SCALE :
          ZoomGridOptions.INITIAL_SCALE;

      if (elementToZoom && elementToZoom?.id === task.id) {
        timeout = setTimeout(() => {
          zoomToElement(`${task.id}`, zoomScale);
        }, 0);
      }
    }

    return () => clearTimeout(timeout);
  }, [task]);

  const zoomToClaimedTask = useCallback(() => {
    if (zoomToElement) {
      zoomToElement(`${taskId}`, ZoomGridOptions.INITIAL_SCALE);
    }
  }, [zoomToElement, taskId]);

  return (
    <>
      {currentTaskTransition.map(
          ({item, props, key}) => item && (
            <animated.div style={props} key={key}>
              <>
                <div className={notRunningStatus ? 'current-task__popup-wrapper' : ''}>
                  {type?.code === TaskCode.COMPLETE_SURVEY ? (
                    <EmployeeSurvey {...taskProps} />
                  ) : (
                    <BaseTask {...taskProps} />
                  )}
                </div>

                {isRunning && successClaimCallback && (
                  <ConfirmationClaimPopup
                    closePopup={handleCloseClaimPopup}
                    task={taskToClaim}
                    successClaimCallback={successClaimCallback}
                    zoomToClaimedTask={zoomToClaimedTask}
                  />
                )}
              </>
            </animated.div>
          )
      )}
    </>
  );
};

export default CurrentTask;
